/**
 * CreateFollowUpModal - Modal para criar/editar follow-ups vinculados a contatos
 * Suporta recorrência (Google Calendar RRULE format)
 * Inclui abas: Informações gerais, Condições e ações, Recorrência
 * Chrome via Modal/ModalHeader/ModalFooter shared (F72), abas via Tabs shared,
 * controles via Input/Select/Textarea shared.
 */

import React, { useState, useEffect } from 'react';
import { useCreateFollowUp, useUpdateFollowUp, useDeleteFollowUp } from '../hooks';
import { useAgents } from '@/modules/automation/ai-agents/hooks/useAgents';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useFunnelsDropdown } from '@/modules/funnels/hooks/useFunnels';
import { useStepsByFunnelId } from '@/modules/funnels/hooks/useFunnelSteps';
import {
  DeleteConfirmationModal,
  Modal,
  ModalHeader,
  ModalFooter,
  Button,
  Tabs,
  Input,
  Select,
  Textarea,
  type TabItem,
  type SelectOption,
} from '@/shared/components/ui';
import type {
  FollowUpEventResponse,
  FollowUpAutomation,
  FollowUpConditionType,
  FollowUpActionType,
  FollowUpRecurrenceInterval
} from '@troia-v3/types';
import { Icon } from '@/shared/icons'
interface CreateFollowUpModalProps {
  contactId: string;
  contactName?: string;
  followUp?: FollowUpEventResponse | null;
  onClose: () => void;
}

type RecurrenceFrequency = 'NONE' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'MINUTES';
type TabType = 'general' | 'conditions' | 'recurrence';

export const CreateFollowUpModal: React.FC<CreateFollowUpModalProps> = ({
  contactId,
  contactName,
  followUp,
  onClose,
}) => {
  const { mutate: createFollowUp, isPending: isCreating } = useCreateFollowUp();
  const { mutate: updateFollowUp, isPending: isUpdating } = useUpdateFollowUp();
  const { mutate: deleteFollowUp, isPending: isDeleting } = useDeleteFollowUp();

  // Verificar se está em ambiente de desenvolvimento (localhost)
  const isDevelopment = window.location.hostname === 'localhost';

  // Buscar agentes de IA
  const { data: agentsData, isLoading: isLoadingAgents } = useAgents({ status: 'active' });

  // Buscar usuários para atribuição
  const { data: usersData, isLoading: isLoadingUsers } = useUsersDropdown({ status: 'active' });

  const isEditMode = !!followUp;
  const isPending = isCreating || isUpdating || isDeleting;

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('general');

  // Buscar funis para ação "mover para etapa"
  const { data: funnelsData, isLoading: isLoadingFunnels } = useFunnelsDropdown();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    recurrence: 'NONE' as RecurrenceFrequency,
    recurrenceInterval: 1,
    recurrenceCount: 0, // 0 = ilimitado
    customIntervalMinutes: 30, // Intervalo customizado em minutos (padrão: 30 minutos)
    // Campos de Condições e Ações
    conditionType: '' as string,
    conditionValue: '' as string,
    actionType: '' as string,
    actionValue: '' as string,
    selectedAgentId: '' as string,
    selectedUserId: '' as string,
    selectedFunnelId: '' as string,
    selectedStepId: '' as string,
  });

  // Buscar etapas do funil selecionado
  const { data: stepsData, isLoading: isLoadingSteps } = useStepsByFunnelId(
    formData.selectedFunnelId || undefined
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Preencher formulário no modo edição
  useEffect(() => {
    if (followUp) {
      const startDate = new Date(followUp.startTime);

      // Formatar data: YYYY-MM-DD
      const dateStr = startDate.toISOString().split('T')[0];

      // Formatar hora: HH:mm
      const timeStr = startDate.toTimeString().slice(0, 5);

      // Parse recurrence (se existir)
      let recurrence: RecurrenceFrequency = 'NONE';
      let recurrenceInterval = 1;
      let recurrenceCount = 0;

      if (followUp.recurrence) {
        // Exemplo: "RRULE:FREQ=DAILY;INTERVAL=1;COUNT=10"
        const rrule = followUp.recurrence[0]; // Primeiro item do array

        if (rrule.includes('FREQ=DAILY')) recurrence = 'DAILY';
        else if (rrule.includes('FREQ=WEEKLY')) recurrence = 'WEEKLY';
        else if (rrule.includes('FREQ=MONTHLY')) recurrence = 'MONTHLY';
        else if (rrule.includes('FREQ=YEARLY')) recurrence = 'YEARLY';

        const intervalMatch = rrule.match(/INTERVAL=(\d+)/);
        if (intervalMatch) recurrenceInterval = parseInt(intervalMatch[1]);

        const countMatch = rrule.match(/COUNT=(\d+)/);
        if (countMatch) recurrenceCount = parseInt(countMatch[1]);
      }

      // Carregar dados de automation se existirem
      let conditionType = '';
      let conditionValue = '';
      let actionType = '';
      let actionValue = '';
      let selectedAgentId = '';
      let selectedUserId = '';
      let selectedFunnelId = '';
      let selectedStepId = '';

      if (followUp.automation) {
        const automation = followUp.automation;

        // Carregar condição
        if (automation.condition) {
          conditionType = automation.condition.type || '';

          // Extrair valor da condição baseado no tipo
          if (automation.condition.config) {
            if (automation.condition.type === 'no_response' || automation.condition.type === 'time_elapsed') {
              conditionValue = automation.condition.config.hours?.toString() || '';
            } else if (automation.condition.type === 'tag_added') {
              conditionValue = automation.condition.config.tagId || '';
            }
          }
        }

        // Carregar ação (primeira ação do array)
        if (automation.actions && automation.actions.length > 0) {
          const firstAction = automation.actions[0];
          actionType = firstAction.type || '';

          // Extrair valor da ação baseado no tipo
          if (firstAction.config) {
            switch (firstAction.type) {
              case 'send_message':
                actionValue = firstAction.config.message || '';
                break;
              case 'send_notification':
                actionValue = firstAction.config.notificationBody || '';
                break;
              case 'assign_user':
                selectedUserId = firstAction.config.userId || '';
                break;
              case 'assign_ai_agent':
                selectedAgentId = firstAction.config.aiAgentId || '';
                break;
              case 'add_tag':
                actionValue = firstAction.config.tagId || '';
                break;
              case 'webhook':
                actionValue = firstAction.config.webhookUrl || '';
                break;
              case 'move_to_step':
                selectedFunnelId = firstAction.config.funnelId || '';
                selectedStepId = firstAction.config.stepId || '';
                break;
            }
          }
        }

        // Atualizar recorrência baseado no automation
        if (automation.recurrenceInterval) {
          const intervalToFrequency: Record<string, RecurrenceFrequency> = {
            'once': 'NONE',
            'hourly': 'HOURLY',
            'daily': 'DAILY',
            'weekly': 'WEEKLY',
            'monthly': 'MONTHLY',
            'minutes': 'MINUTES',
          };
          recurrence = intervalToFrequency[automation.recurrenceInterval] || 'NONE';
        }

        // Carregar maxExecutions se existir
        if (automation.maxExecutions) {
          recurrenceCount = automation.maxExecutions;
        }
      }

      setFormData({
        title: followUp.summary || '',
        description: followUp.description || '',
        date: dateStr,
        time: timeStr,
        recurrence,
        recurrenceInterval,
        recurrenceCount,
        customIntervalMinutes: followUp.automation?.customIntervalMinutes || 30,
        conditionType,
        conditionValue,
        actionType,
        actionValue,
        selectedAgentId,
        selectedUserId,
        selectedFunnelId,
        selectedStepId,
      });
    }
  }, [followUp]);

  const handleDelete = () => {
    if (!followUp) return;

    deleteFollowUp(followUp.id, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        onClose();
      },
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    }
    if (!formData.date) {
      newErrors.date = 'Data é obrigatória';
    }
    if (!formData.time) {
      newErrors.time = 'Hora é obrigatória';
    }
    if (formData.recurrence !== 'NONE') {
      if (formData.recurrenceInterval < 1) {
        newErrors.recurrenceInterval = 'Intervalo deve ser maior que 0';
      }
      if (formData.recurrenceCount < 0) {
        newErrors.recurrenceCount = 'Quantidade de ocorrências inválida';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Combinar data e hora
    const startTime = new Date(`${formData.date}T${formData.time}`);
    const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // +1 hora

    // Para follow-ups, NÃO usamos RRULE do calendário para recorrências de minutos/horas
    // A recorrência de automação é controlada pelo campo automation.recurrenceInterval
    // RRULE do calendário só é usado para recorrências diárias ou maiores (compatíveis com RFC 5545)
    let recurrence: string[] | undefined = undefined;

    // Apenas gerar RRULE para frequências suportadas pelo padrão RFC 5545
    const rruleCompatibleFrequencies: RecurrenceFrequency[] = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
    if (rruleCompatibleFrequencies.includes(formData.recurrence)) {
      let rrule = `RRULE:FREQ=${formData.recurrence}`;

      if (formData.recurrenceInterval > 1) {
        rrule += `;INTERVAL=${formData.recurrenceInterval}`;
      }

      if (formData.recurrenceCount > 0) {
        rrule += `;COUNT=${formData.recurrenceCount}`;
      }

      recurrence = [rrule];
    }

    // Construir objeto automation a partir dos campos do formulário
    let automation: FollowUpAutomation | undefined = undefined;

    // Verificar se há condição ou ação configurada
    const hasCondition = formData.conditionType !== '';
    const hasAction = formData.actionType !== '';

    // SEMPRE criar automation se houver recorrência configurada (mesmo sem condição/ação)
    // Isso permite follow-ups com apenas recorrência de execução
    const hasRecurrence = formData.recurrence !== 'NONE';

    if (hasCondition || hasAction || hasRecurrence) {
      // Mapear recurrence para recurrenceInterval
      const recurrenceIntervalMap: Record<RecurrenceFrequency, FollowUpRecurrenceInterval> = {
        'NONE': 'once',
        'HOURLY': 'hourly',
        'DAILY': 'daily',
        'WEEKLY': 'weekly',
        'MONTHLY': 'monthly',
        'YEARLY': 'monthly', // Yearly não existe no enum, usar monthly como fallback
        'MINUTES': 'minutes'
      };

      automation = {
        status: 'active', // Sempre ativar ao criar/editar

        // Condição (se configurada)
        condition: hasCondition ? {
          type: formData.conditionType as FollowUpConditionType,
          config: {
            // Para condições de tempo, converter valor para horas
            ...((['no_response', 'time_elapsed'].includes(formData.conditionType) && formData.conditionValue) && {
              hours: parseInt(formData.conditionValue) || 24
            }),
            // Para condição de tag
            ...(formData.conditionType === 'tag_added' && formData.conditionValue && {
              tagId: formData.conditionValue
            })
          }
        } : undefined,

        // Ações (se configurada)
        actions: hasAction ? [{
          type: formData.actionType as FollowUpActionType,
          config: {
            // Mensagem para send_message
            ...(formData.actionType === 'send_message' && formData.actionValue && {
              message: formData.actionValue
            }),
            // Notificação
            ...(formData.actionType === 'send_notification' && formData.actionValue && {
              notificationTitle: 'Follow-up Reminder',
              notificationBody: formData.actionValue
            }),
            // Atribuir usuário
            ...(formData.actionType === 'assign_user' && formData.selectedUserId && {
              userId: formData.selectedUserId
            }),
            // Atribuir agente de IA
            ...(formData.actionType === 'assign_ai_agent' && formData.selectedAgentId && {
              aiAgentId: formData.selectedAgentId,
              ...(formData.actionValue && { instruction: formData.actionValue })
            }),
            // Adicionar tag
            ...(formData.actionType === 'add_tag' && formData.actionValue && {
              tagId: formData.actionValue
            }),
            // Webhook
            ...(formData.actionType === 'webhook' && formData.actionValue && {
              webhookUrl: formData.actionValue
            }),
            // Mover para etapa
            ...(formData.actionType === 'move_to_step' && formData.selectedFunnelId && formData.selectedStepId && {
              funnelId: formData.selectedFunnelId,
              stepId: formData.selectedStepId
            })
          }
        }] : undefined,

        // Recorrência de automação (controla execução de ações, não calendário)
        recurrenceInterval: recurrenceIntervalMap[formData.recurrence],

        // Custom interval para MINUTES (em minutos) e HOURLY (em horas)
        ...(formData.recurrence === 'MINUTES' && formData.recurrenceInterval > 0 && {
          customIntervalMinutes: formData.recurrenceInterval
        }),
        ...(formData.recurrence === 'HOURLY' && formData.recurrenceInterval > 0 && {
          customIntervalMinutes: formData.recurrenceInterval * 60 // Converter horas para minutos
        }),

        // Max executions (se recurrence count > 0)
        ...(formData.recurrenceCount > 0 && {
          maxExecutions: formData.recurrenceCount
        }),

        // Inicializar contadores
        executionCount: 0
      };
    }

    const eventData = {
      summary: formData.title,
      description: formData.description || undefined,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      eventType: 'follow_up' as const,
      contactId,
      recurrence,
      automation, // Incluir automation no payload
    };

    try {
      if (isEditMode && followUp) {
        updateFollowUp({
          id: followUp.id,
          data: eventData,
        });
      } else {
        createFollowUp(eventData);
      }
      onClose();
    } catch (error) {
      console.error('Erro ao salvar follow-up:', error);
    }
  };

  // ── Opções dos selects (NUNCA <option> como children — prop options) ────
  const conditionOptions: SelectOption[] = [
    { value: 'no_response', label: 'Sem resposta do cliente' },
    { value: 'no_event_scheduled', label: 'Sem evento agendado' },
    { value: 'time_elapsed', label: 'Tempo decorrido sem interações' },
    ...(isDevelopment ? [{ value: 'tag_added', label: 'Tag adicionada (Em desenvolvimento)' }] : []),
  ];

  const actionOptions: SelectOption[] = [
    { value: 'send_message', label: 'Enviar mensagem' },
    ...(isDevelopment ? [{ value: 'send_notification', label: 'Enviar notificação (Em desenvolvimento)' }] : []),
    { value: 'assign_user', label: 'Atribuir usuário' },
    ...(isDevelopment ? [{ value: 'assign_ai_agent', label: 'Atribuir agente de IA (Em desenvolvimento)' }] : []),
    ...(isDevelopment ? [{ value: 'add_tag', label: 'Adicionar tag (Em desenvolvimento)' }] : []),
    ...(isDevelopment ? [{ value: 'webhook', label: 'Executar webhook (Em desenvolvimento)' }] : []),
    { value: 'move_to_step', label: 'Mover para etapa' },
  ];

  const recurrenceOptions: SelectOption[] = [
    { value: 'NONE', label: 'Sem recorrência' },
    { value: 'MINUTES', label: 'Intervalo em minutos' },
    { value: 'HOURLY', label: 'Intervalo em horas' },
    { value: 'DAILY', label: 'Diariamente' },
    { value: 'WEEKLY', label: 'Semanalmente' },
    { value: 'MONTHLY', label: 'Mensalmente' },
    { value: 'YEARLY', label: 'Anualmente' },
  ];

  // ── Painéis das abas ────────────────────────────────────────────────────

  const generalPanel = (
    <div className="p-4 space-y-4" data-testid="followup-tab-general-panel">
      {/* Título */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
          <Icon name="file-text" className="w-4 h-4 inline mr-1" />
          Título *
        </label>
        <Input
          type="text"
          value={formData.title}
          onChange={(e) => {
            setFormData({ ...formData, title: e.target.value });
            setErrors({ ...errors, title: '' });
          }}
          placeholder="Ex: Ligar para cliente"
          error={errors.title || undefined}
          data-testid="followup-title-input"
        />
        {errors.title && (
          <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }}>
            {errors.title}
          </p>
        )}
      </div>

      {/* Data */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
          <Icon name="calendar" className="w-4 h-4 inline mr-1" />
          Data *
        </label>
        <Input
          type="date"
          value={formData.date}
          onChange={(e) => {
            setFormData({ ...formData, date: e.target.value });
            setErrors({ ...errors, date: '' });
          }}
          error={errors.date || undefined}
          data-testid="followup-date-input"
        />
        {errors.date && (
          <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }}>
            {errors.date}
          </p>
        )}
      </div>

      {/* Hora */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
          <Icon name="clock" className="w-4 h-4 inline mr-1" />
          Hora *
        </label>
        <Input
          type="time"
          value={formData.time}
          onChange={(e) => {
            setFormData({ ...formData, time: e.target.value });
            setErrors({ ...errors, time: '' });
          }}
          error={errors.time || undefined}
          data-testid="followup-time-input"
        />
        {errors.time && (
          <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }}>
            {errors.time}
          </p>
        )}
      </div>

      {/* Descrição (Opcional) */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
          <Icon name="align-left" className="w-4 h-4 inline mr-1" />
          Descrição (Opcional)
        </label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Detalhes sobre o retorno…"
          rows={3}
          resize="none"
          data-testid="followup-description-input"
        />
      </div>
    </div>
  );

  const conditionsPanel = (
    <div className="p-4 space-y-4" data-testid="followup-tab-conditions-panel">
      {/* Seção de Condições */}
      <div
        className="rounded-lg border p-4"
        style={{ backgroundColor: 'var(--theme-button-ghost-hover)', borderColor: 'var(--theme-card-border)' }}
      >
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
          <Icon name="zap" className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
          Condições
        </h3>
        <p className="text-xs mb-4" style={{ color: 'var(--theme-muted-text)' }}>
          Defina quando este retorno deve ser acionado automaticamente.
        </p>

        {/* Tipo de Condição */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
            Tipo de condição
          </label>
          <Select
            value={formData.conditionType}
            onChange={(value) => setFormData({ ...formData, conditionType: value, conditionValue: '' })}
            options={conditionOptions}
            placeholder="Selecione uma condição"
            data-testid="followup-condition-type"
          />
        </div>

        {/* Valor da Condição - Não mostrar para no_event_scheduled */}
        {formData.conditionType && formData.conditionType !== 'no_event_scheduled' && (
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
              {formData.conditionType === 'no_response' && 'Após quantas horas sem resposta'}
              {formData.conditionType === 'time_elapsed' && 'Tempo em horas'}
              {formData.conditionType === 'tag_added' && 'Nome da tag'}
            </label>
            <Input
              type={formData.conditionType === 'no_response' || formData.conditionType === 'time_elapsed' ? 'number' : 'text'}
              value={formData.conditionValue}
              onChange={(e) => setFormData({ ...formData, conditionValue: e.target.value })}
              placeholder={
                formData.conditionType === 'no_response' ? 'Ex: 24'
                : formData.conditionType === 'time_elapsed' ? 'Ex: 48'
                : formData.conditionType === 'tag_added' ? 'Ex: prioritário'
                : ''
              }
              data-testid="followup-condition-value"
            />
          </div>
        )}
      </div>

      {/* Seção de Ações */}
      <div
        className="rounded-lg border p-4"
        style={{ backgroundColor: 'var(--theme-button-ghost-hover)', borderColor: 'var(--theme-card-border)' }}
      >
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
          <Icon name="repeat" className="w-4 h-4" style={{ color: 'var(--theme-success-color)' }} />
          Ações
        </h3>
        <p className="text-xs mb-4" style={{ color: 'var(--theme-muted-text)' }}>
          Defina o que acontece quando a condição for atendida.
        </p>

        {/* Tipo de Ação */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
            Tipo de ação
          </label>
          <Select
            value={formData.actionType}
            onChange={(value) =>
              setFormData({ ...formData, actionType: value, actionValue: '', selectedAgentId: '', selectedUserId: '', selectedFunnelId: '', selectedStepId: '' })
            }
            options={actionOptions}
            placeholder="Selecione uma ação"
            data-testid="followup-action-type"
          />
        </div>

        {/* Seleção de Agente de IA */}
        {formData.actionType === 'assign_ai_agent' && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
              <Icon name="cpu" className="w-4 h-4 inline mr-1" />
              Selecione o Agente de IA
            </label>
            <Select
              value={formData.selectedAgentId}
              onChange={(value) => setFormData({ ...formData, selectedAgentId: value })}
              options={(agentsData?.items ?? []).map((agent) => ({ value: agent.id, label: agent.name }))}
              placeholder={isLoadingAgents ? 'Carregando agentes...' : 'Selecione um agente'}
              disabled={isLoadingAgents}
              data-testid="followup-agent-select"
            />
            {agentsData?.items?.length === 0 && !isLoadingAgents && (
              <p className="text-xs mt-1" style={{ color: 'var(--theme-warning-color)' }}>
                Nenhum agente de IA ativo encontrado. Crie um agente primeiro.
              </p>
            )}
          </div>
        )}

        {/* Seleção de Usuário */}
        {formData.actionType === 'assign_user' && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
              <Icon name="user" className="w-4 h-4 inline mr-1" />
              Selecione o Usuário
            </label>
            <Select
              value={formData.selectedUserId}
              onChange={(value) => setFormData({ ...formData, selectedUserId: value })}
              options={(usersData ?? []).map((user) => ({ value: user.id, label: `${user.name} (${user.email})` }))}
              placeholder={isLoadingUsers ? 'Carregando usuários...' : 'Selecione um usuário'}
              disabled={isLoadingUsers}
              data-testid="followup-user-select"
            />
            {usersData?.length === 0 && !isLoadingUsers && (
              <p className="text-xs mt-1" style={{ color: 'var(--theme-warning-color)' }}>
                Nenhum usuário ativo encontrado.
              </p>
            )}
          </div>
        )}

        {/* Seleção de Funil e Etapa (move_to_step) */}
        {formData.actionType === 'move_to_step' && (
          <>
            {/* Seleção de Funil */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
                <Icon name="layers" className="w-4 h-4 inline mr-1" />
                Selecione o Funil
              </label>
              <Select
                value={formData.selectedFunnelId}
                onChange={(value) => setFormData({ ...formData, selectedFunnelId: value, selectedStepId: '' })}
                options={(funnelsData ?? []).map((funnel) => ({ value: funnel.id, label: funnel.name }))}
                placeholder={isLoadingFunnels ? 'Carregando funis...' : 'Selecione um funil'}
                disabled={isLoadingFunnels}
                data-testid="followup-funnel-select"
              />
              {funnelsData?.length === 0 && !isLoadingFunnels && (
                <p className="text-xs mt-1" style={{ color: 'var(--theme-warning-color)' }}>
                  Nenhum funil ativo encontrado.
                </p>
              )}
            </div>

            {/* Seleção de Etapa (dependente do funil) */}
            {formData.selectedFunnelId && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
                  <Icon name="layers" className="w-4 h-4 inline mr-1" />
                  Selecione a Etapa
                </label>
                <Select
                  value={formData.selectedStepId}
                  onChange={(value) => setFormData({ ...formData, selectedStepId: value })}
                  options={(stepsData ?? []).map((step) => ({ value: step.id, label: step.name }))}
                  placeholder={isLoadingSteps ? 'Carregando etapas...' : 'Selecione uma etapa'}
                  disabled={isLoadingSteps}
                  data-testid="followup-step-select"
                />
                {stepsData?.length === 0 && !isLoadingSteps && (
                  <p className="text-xs mt-1" style={{ color: 'var(--theme-warning-color)' }}>
                    Nenhuma etapa encontrada neste funil.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* Valor da Ação */}
        {formData.actionType && formData.actionType !== 'assign_ai_agent' && formData.actionType !== 'assign_user' && formData.actionType !== 'move_to_step' && (
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
              {formData.actionType === 'send_message' && 'Mensagem a enviar'}
              {formData.actionType === 'send_notification' && 'Texto da notificação'}
              {formData.actionType === 'add_tag' && 'Nome da tag'}
              {formData.actionType === 'webhook' && 'URL do webhook'}
            </label>
            {formData.actionType === 'send_message' ? (
              <Textarea
                value={formData.actionValue}
                onChange={(e) => setFormData({ ...formData, actionValue: e.target.value })}
                placeholder="Digite a mensagem que será enviada…"
                rows={3}
                resize="none"
                data-testid="followup-action-value"
              />
            ) : (
              <Input
                type={formData.actionType === 'webhook' ? 'url' : 'text'}
                value={formData.actionValue}
                onChange={(e) => setFormData({ ...formData, actionValue: e.target.value })}
                placeholder={
                  formData.actionType === 'send_notification' ? 'Ex: Cliente precisa de atenção!'
                  : formData.actionType === 'add_tag' ? 'Ex: follow_up'
                  : formData.actionType === 'webhook' ? 'Ex: https://api.exemplo.com/webhook'
                  : ''
                }
                data-testid="followup-action-value"
              />
            )}
          </div>
        )}

        {/* Instrução ao Agente (quando Agente de IA selecionado) */}
        {formData.actionType === 'assign_ai_agent' && formData.selectedAgentId && (
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
              Instrução ao agente
            </label>
            <Textarea
              value={formData.actionValue}
              onChange={(e) => setFormData({ ...formData, actionValue: e.target.value })}
              placeholder="Ex: Reengajar o cliente perguntando sobre o interesse…"
              rows={3}
              resize="none"
              data-testid="followup-agent-instruction"
            />
            <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
              Esta instrução será passada ao agente de IA para contextualizar a ação.
            </p>
          </div>
        )}
      </div>

      {/* Informação adicional */}
      <div
        className="flex items-start gap-3 p-3 rounded-lg"
        style={{ backgroundColor: 'var(--theme-info-subtle)', border: '1px solid var(--theme-card-border)' }}
      >
        <Icon name="info" className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--theme-info-fg)' }} />
        <div>
          <p className="text-xs" style={{ color: 'var(--theme-info-fg)' }}>
            <strong>Dica:</strong> As condições e ações são opcionais. Se não forem preenchidas,
            o retorno funcionará apenas como um lembrete simples na data agendada.
          </p>
        </div>
      </div>
    </div>
  );

  const recurrencePanel = (
    <div className="p-4 space-y-4" data-testid="followup-tab-recurrence-panel">
      {/* Recorrência */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
          <Icon name="repeat" className="w-4 h-4 inline mr-1" />
          Tipo de recorrência
        </label>
        <Select
          value={formData.recurrence}
          onChange={(value) => setFormData({ ...formData, recurrence: value as RecurrenceFrequency })}
          options={recurrenceOptions}
          data-testid="followup-recurrence-type"
        />
      </div>

      {/* Configurações de Recorrência */}
      {formData.recurrence !== 'NONE' && (
        <>
          {/* Intervalo */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
              Repetir a cada
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="1"
                value={formData.recurrenceInterval}
                fullWidth={false}
                className="w-20"
                onChange={(e) => {
                  setFormData({ ...formData, recurrenceInterval: parseInt(e.target.value) || 1 });
                  setErrors({ ...errors, recurrenceInterval: '' });
                }}
                error={errors.recurrenceInterval || undefined}
                data-testid="followup-recurrence-interval"
              />
              <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                {formData.recurrence === 'HOURLY' && 'hora(s)'}
                {formData.recurrence === 'DAILY' && 'dia(s)'}
                {formData.recurrence === 'WEEKLY' && 'semana(s)'}
                {formData.recurrence === 'MONTHLY' && 'mês(es)'}
                {formData.recurrence === 'YEARLY' && 'ano(s)'}
                {formData.recurrence === 'MINUTES' && 'minuto(s)'}
              </span>
            </div>
            {errors.recurrenceInterval && (
              <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }}>
                {errors.recurrenceInterval}
              </p>
            )}
          </div>

          {/* Quantidade de Ocorrências */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)' }}>
              Número de ocorrências (0 = ilimitado)
            </label>
            <Input
              type="number"
              min="0"
              value={formData.recurrenceCount}
              onChange={(e) => {
                setFormData({ ...formData, recurrenceCount: parseInt(e.target.value) || 0 });
                setErrors({ ...errors, recurrenceCount: '' });
              }}
              error={errors.recurrenceCount || undefined}
              data-testid="followup-recurrence-count"
            />
            {errors.recurrenceCount && (
              <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }}>
                {errors.recurrenceCount}
              </p>
            )}
          </div>
        </>
      )}

      {/* Informação sobre recorrência */}
      <div
        className="flex items-start gap-3 p-3 rounded-lg"
        style={{ backgroundColor: 'var(--theme-success-subtle)', border: '1px solid var(--theme-card-border)' }}
      >
        <Icon name="info" className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--theme-success-fg)' }} />
        <div>
          <p className="text-xs" style={{ color: 'var(--theme-success-fg)' }}>
            <strong>Sobre recorrência:</strong> Configure para que o retorno se repita automaticamente.
            Útil para lembretes periódicos como follow-ups semanais ou mensais.
          </p>
        </div>
      </div>
    </div>
  );

  const tabs: TabItem[] = [
    { id: 'general', label: 'Informações gerais', icon: 'info', content: generalPanel },
    { id: 'conditions', label: 'Condições e ações', icon: 'zap', content: conditionsPanel },
    { id: 'recurrence', label: 'Recorrência', icon: 'repeat', content: recurrencePanel },
  ];

  return (
    <>
      <Modal isOpen onClose={onClose} maxWidth="xl">
        <ModalHeader title={isEditMode ? 'Editar Retorno' : 'Novo Retorno'} onClose={onClose}>
          {contactName && (
            <span className="text-sm truncate" style={{ color: '#64748b' }}>
              Contato: {contactName}
            </span>
          )}
        </ModalHeader>

        {/* Form envolve as abas para preservar submit por Enter */}
        <form
          id="followup-form"
          onSubmit={handleSubmit}
          className="flex flex-col"
          style={{ maxHeight: '65vh' }}
        >
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={(tabId) => setActiveTab(tabId as TabType)}
            className="px-4 overflow-x-auto"
          />
        </form>

        <ModalFooter>
          {/* Botão Excluir (apenas no modo edição) */}
          {isEditMode && (
            <button
              type="button"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isPending}
              className="p-2 rounded-lg transition-colors"
              style={{ color: 'var(--theme-error-color)', backgroundColor: 'transparent' }}
              title="Excluir retorno"
              data-testid="followup-delete-button"
            >
              <Icon name="trash" className="w-5 h-5" />
            </button>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isPending}
            data-testid="followup-cancel-button"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="followup-form"
            variant="primary"
            icon="calendar"
            loading={isPending}
            disabled={isPending}
            data-testid="followup-submit-button"
          >
            {isEditMode ? 'Salvar Alterações' : 'Criar Retorno'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationModal
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Excluir Retorno"
        message={`Tem certeza que deseja excluir o retorno "${followUp?.summary || 'sem título'}"?`}
        warningMessage="Esta ação não pode ser desfeita. O retorno será permanentemente removido."
        isLoading={isDeleting}
        confirmButtonText="Excluir Retorno"
      />
    </>
  );
};
